import { Category, LocalizedString } from '@synergy/models';
import { EditIcon, EyeIcon, EyeSlashIcon, PlusCircleIcon, TrashIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { PoolMenu, PoolMenuItem } from '@synergy/components';
import { TableCell, TableRow, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';

import { CategoryConfigurationRowEclass } from './category-configuration-row-eclass';
import { PaloDisclosureArrow } from '../../../../../common/components/palo/palo-disclosure-arrow';
import { TranslatedField } from '../../../components/translated-field';
import { TranslationPill } from '../../../components/translation-pill';
import { VisibilityIcon } from '../../../components/visibility-icon';
import { selectedLanguageSelector } from '../../../../../common/selectors/selected-language-selectors';
import { toggleCategoryVisibility } from '../../../../../common/actions/category-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    icon: {
        fontSize: 16,
        color: theme.palette.common.black,
    },
    defaultLanguage: {
        color: '#979797',
    },
    content: {
        display: 'flex',
        alignItems: 'center',
    },
    clickable: {
        cursor: 'pointer',
    },
}));

export interface CategoryConfigurationRowProps {
    categories: Category[];
    category: Category;
    filterIds?: number[];
    unavailableEclasses: number[];
    depth: number;
    onDelete: (item: Category) => void;
    onEdit: (item: Category, depth: number, unavailableEclasses: number[]) => void;
}

export const CategoryConfigurationRow = (props: CategoryConfigurationRowProps) => {
    const classes = useStyles();
    const [isExpanded, setIsExpanded] = useState(false);
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const dispatch = useThunkDispatch();

    useEffect(() => {
        setIsExpanded(!!props.filterIds);
    }, [props.filterIds]);

    const childCategories = props.categories.filter(category => category.parentId === props.category.id);
    const childEclasses = childCategories.flatMap(childCategory => childCategory.eclassPositions || []);
    const isExpandable = childCategories.length > 0;
    const indent = props.depth * 48;

    if (props.filterIds && !props.filterIds.includes(props.category.id)) {
        return null;
    }

    const toggle = () => setIsExpanded(!isExpanded);

    const handleCreate = () => {
        props.onEdit(
            {
                id: -1,
                parentId: props.category.id,
                isVisibleInShop: props.category.isVisibleInShop,
                translations: {} as LocalizedString,
            },
            props.depth,
            [...props.unavailableEclasses, ...childEclasses, ...(props.category.eclassPositions ?? [])]
        );
    };

    return (
        <>
            <TableRow>
                <TableCell
                    onClick={isExpandable ? toggle : undefined}
                    className={isExpandable ? classes.clickable : ''}
                >
                    <div className={classes.content}>
                        <div style={{ width: indent }} />
                        {isExpandable && <PaloDisclosureArrow open={isExpanded} />}
                        <div style={{ width: isExpandable ? 32 : 56 }} />
                        <TranslatedField value={props.category.translations} selectedLocale={selectedLanguage} />
                        {props.category.eclassLabel && <span>&nbsp;({props.category.eclassLabel})</span>}
                    </div>
                </TableCell>
                <TableCell>
                    <CategoryConfigurationRowEclass eclassPositions={props.category.eclassPositions} />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                    <VisibilityIcon
                        isVisible={props.category.isVisibleInShop}
                        isVisibleLabel={<FormattedMessage id="app.tooltip.showInShop" />}
                        isNotVisibleLabel={<FormattedMessage id="app.tooltip.hideInShop" />}
                    />
                </TableCell>
                <TableCell>
                    <TranslationPill value={props.category.translations} locales={availableLocales} />
                </TableCell>
                <TableCell>
                    <PoolMenu menuPosition="right">
                        <PoolMenuItem
                            isExtendedMenu={true}
                            icon={
                                props.category.isVisibleInShop ? (
                                    <EyeSlashIcon className={classes.icon} />
                                ) : (
                                    <EyeIcon className={classes.icon} />
                                )
                            }
                            title={
                                <FormattedMessage
                                    id={
                                        props.category.isVisibleInShop
                                            ? 'app.configuration.hideInShop'
                                            : 'app.configuration.showInShop'
                                    }
                                />
                            }
                            onClick={() => dispatch(toggleCategoryVisibility(props.category))}
                        />
                        <PoolMenuItem
                            isExtendedMenu={true}
                            icon={<EditIcon className={classes.icon} />}
                            title={<FormattedMessage id="app.common.edit" />}
                            onClick={() =>
                                props.onEdit(props.category, props.depth, [
                                    ...props.unavailableEclasses,
                                    ...childEclasses,
                                ])
                            }
                        />
                        <PoolMenuItem
                            isExtendedMenu={true}
                            icon={<PlusCircleIcon className={classes.icon} />}
                            title={<FormattedMessage id="app.configuration.createSubCategory" />}
                            onClick={handleCreate}
                        />
                        <PoolMenuItem
                            isExtendedMenu={true}
                            icon={<TrashIcon className={classes.icon} />}
                            title={<FormattedMessage id="app.common.delete" />}
                            onClick={() => props.onDelete(props.category)}
                        />
                    </PoolMenu>
                </TableCell>
            </TableRow>
            {isExpandable &&
                isExpanded &&
                childCategories.map(childCategory => (
                    <CategoryConfigurationRow
                        key={childCategory.id}
                        categories={props.categories}
                        category={childCategory}
                        filterIds={props.filterIds}
                        depth={props.depth + 1}
                        unavailableEclasses={[...props.unavailableEclasses, ...(props.category.eclassPositions ?? [])]}
                        onEdit={props.onEdit}
                        onDelete={props.onDelete}
                    />
                ))}
        </>
    );
};
